/* eslint-disable complexity */
import React, { useContext, useEffect, useState } from 'react';
import {
	IOrderInfoDetails,
	OfferContext,
	VignetteContext,
	getDecimalPriceAmountByCatalogId,
	getPriceAmountByCatalogId,
	getLabelValue,
	ITypeVignettesOrderManagementFields,
} from '@adac-mp/shared';
import Skeleton from 'react-loading-skeleton';
import {
	Typography,
	CamperIcon,
	CarIcon,
	MotorCycleIcon,
	DropdownArrowDown,
	TollSectionInfo,
	HintMessageV2,
	Hint,
	getHintContent,
} from '../../index';
import { useViewport } from '../../hooks/useViewport';

interface IProductOrderSummaryProps {
	category?: { moduleDetail: string; moduleDetailId: number };
	children?: React.ReactNode;
	className?: string;
	wrapperClassName?: string;
	icon: React.ReactNode;
	order: IOrderInfoDetails;
	simple?: boolean;
	style?: React.CSSProperties;
	title?: string | React.ReactNode;
	expandable?: boolean;
}

export const ProductOrderSummary = ({
	simple = false,
	order,
	title,
	icon: Icon,
	children,
	className,
	wrapperClassName,
	category,
	style = {},
	expandable = true,
}: IProductOrderSummaryProps) => {
	const { viewport } = useViewport();
	const {
		moduleTypeId,
		sectionName,
		trips,
		refundAmount,
		routeDirection,
		changelicenseplate,
		changevalidity,
		cancelable,
		cancelled,
		paymentNotSuccessful,
		flexStatus,
		flexProduct,
		supplierId,
		bazgStatus,
	} = order;
	const isVignette = moduleTypeId === 2;
	const isFlexService = moduleTypeId === 4;
	const isDesktopView = viewport === 'desktop';
	const { contentfulLabels } = useContext(VignetteContext);
	const currentLabels = contentfulLabels as ITypeVignettesOrderManagementFields;
	const [isExpanded, setIsExpanded] = useState(true);
	const [animate, setAnimate] = useState<boolean | undefined>(true);
	const { labels } = useContext(OfferContext);

	useEffect(() => {
		if (isExpanded) {
			setAnimate(true);
		}
	}, [isExpanded]);

	const iconMap = {
		2: <CarIcon className="inline mr-1.5" />,
		3: <MotorCycleIcon className="inline mr-1.5" />,
		4: <CamperIcon className="inline mr-1.5" />,
	};

	const totalPrice = cancelled
		? `${(getDecimalPriceAmountByCatalogId(order.priceComponents) + refundAmount?.decimalAmount).toFixed(
				2,
		  )}`.replace('.', ',')
		: getPriceAmountByCatalogId(order.priceComponents);

	const renderHintMessage = (isEditable: boolean): Hint[] =>
		isEditable
			? [
					{
						tip: 'Hinweis:',
						message: 'Die Wiederrufsfrist ist bereits abgelaufen. Eine Stornierung ist nicht mehr möglich.',
					},
			  ]
			: [
					{
						message:
							'Bestellung nicht mehr änderbar. Es sind keine Änderungen am Produkt mehr möglich, da der Gültigkeitszeitraum bereits überschritten ist.',
					},
			  ];

	return (
		<div
			key={`${order?.moduleType} section-${order?.serviceCatalogCode}`}
			data-cy={`${order?.moduleType} section-${order?.serviceCatalogCode}`}
			className={`${wrapperClassName} w-full ease-in-out duration-500 rounded border border-solid border-transparent ${
				!simple ? 'hover:border-gray-400' : ''
			}`}
			style={style}>
			<div
				className={`flex flex-col gap-4 py-3 rounded-t border border-transparent md:py-4 ${
					isExpanded ? '' : 'rounded-b hover:border-gray-400'
				} ${simple ? 'rounded-b bg-gray-700' : 'bg-white px-1.5 cursor-pointer'} ${className}`}
				onClick={() => {
					if (expandable) {
						if (!isExpanded) {
							setIsExpanded(true);
						} else {
							setAnimate(false);
						}
					}
				}}>
				<div className="flex items-center gap-4 px-3">
					{isDesktopView && Icon}
					<div className="flex flex-col gap-1.5">
						<Typography component="h3" color="title" variant="title" size="xl">
							{title}
						</Typography>
						{isVignette && category && (
							<div className="flex items-center">
								{iconMap[category?.moduleDetailId]}
								<Typography color="lightText">{category?.moduleDetail}</Typography>
							</div>
						)}
						{isFlexService && (
							<div className="flex items-center">
								<Typography color="title">
									{contentfulLabels?.cart?.allTollRoutesUsable ||
										'Alle Streckenmautabschnitte nutzbar'}
								</Typography>
							</div>
						)}
						<TollSectionInfo
							trips={trips}
							sectionName={sectionName}
							labels={labels}
							routeDirection={routeDirection}
							moduleTypeId={moduleTypeId}
						/>
					</div>
					<div className="flex-1 inline-flex justify-end gap-2">
						{!isFlexService && (
							<span
								className={`inline-flex self-center ${
									simple ? 'flex-col text-right gap-1.5' : 'flex-row'
								}`}>
								<Typography size="xlEven" variant="title" color="title">
									{totalPrice ? `${totalPrice}€` : <Skeleton inline height="100%" width="100%" />}
								</Typography>
								{simple && (
									<Typography className="inline-flex whitespace-nowrap" color="lightText">
										{getLabelValue('mp.vat', labels) || 'inkl. MwSt.'}
									</Typography>
								)}
							</span>
						)}

						{!simple && (
							<button
								className={`cursor-pointer border-none bg-white inline-flex h-fit transition-transform duration-300 ${
									isExpanded && animate && 'rotate-180'
								}`}>
								<DropdownArrowDown />
							</button>
						)}
					</div>
				</div>
			</div>
			{isExpanded && (
				<div
					className={`bg-white rounded-b ${animate ? 'animate-fadeSlideInFast' : 'animate-fadeSlideOutFast'}`}
					onAnimationEnd={() => {
						if (!animate) {
							setIsExpanded(false);
						}
					}}>
					{/*SLO & AT*/}
					{supplierId !== 14 && (
						<>
							{cancelled && !paymentNotSuccessful && (flexStatus === 5 || !flexProduct) && (
								<HintMessageV2
									className="mx-1.5 mb-3.5"
									variant="success"
									hints={
										isFlexService
											? getHintContent(
												'deletedFlexProduct',
												[
													{
														message:
															'Dieser Service wurde erfolgreich storniert. Es erfolgen keine Abbuchungen auf das beim Kauf verwendete Zahlungsmittel.',
													},
												],
												currentLabels?.hintMessages,
											)
											: getHintContent(
												'deletedProduct',
												[
													{
														message:
															'Dieses Produkt wurde storniert. Die Rückerstattung erfolgt auf das beim Kauf verwendete Zahlungsmittel.',
													},
												],
												currentLabels?.hintMessages,
											)
									}
								/>
							)}

							{flexProduct && paymentNotSuccessful && flexStatus !== 0 && flexStatus !== 4 && (
								<HintMessageV2
									className="mx-1.5 mb-3.5"
									variant="error"
									hints={
										flexStatus === 5 || flexStatus === 3
											? getHintContent(
												'paymentNotSuccessfulTerminated',
												[
													{
														message:
															'Der Streckenmaut Flex Service wurde automatisch widerrufen, da die Abbuchung nicht erfolgreich war. Mit diesem Kennzeichen erfolgen keine automatischen Abbuchungen mehr.',
													},
												],
												currentLabels?.hintMessages,
											)
											: getHintContent(
												'paymentNotSuccessful',
												[
													{
														message:
															'Die letzte automatische Abbuchung der Mautgebühren war nicht erfolgreich. Bitte aktualisieren Sie Ihre Zahlungsart.',
													},
												],
												currentLabels?.hintMessages,
											)
									}
								/>
							)}

							{!cancelled && cancelable && !flexProduct && (
								<HintMessageV2
									className="mx-1.5 mb-3.5"
									variant="complete"
									hints={getHintContent(
										'productOrderConfirmedAndCancelable',
										[
											{
												message:
													'Bestellung erfolgreich abgeschlossen. Es sind aktuell Änderungen am Produkt möglich, da die Gültigkeit bzw. der Gültigkeitszeitraum noch nicht begonnen hat.',
											},
										],
										currentLabels?.hintMessages,
									)}
								/>
							)}

							{!cancelled && !cancelable && (
								<HintMessageV2
									className="mx-1.5 mb-3.5"
									variant="info"
									hints={getHintContent(
										changelicenseplate || changevalidity ? 'productNotCancelable' : 'productNotAmendable',
										renderHintMessage(changelicenseplate || changevalidity),
										currentLabels?.hintMessages,
									)}
								/>
							)}
						</>
					)}

					{/*CH*/}
					{supplierId === 14 && (
						<>
							{bazgStatus === 'cancelled' && (
								<HintMessageV2
									className="mx-1.5 mb-3.5"
									variant="error"
									hints={getHintContent(
										'swissProductOrderCancelled',
										[
											{
												message:
													'Bestellung Fehlgeschlagen und Storniert. Leider war der kauf einer Schweizer Jahresvignette nicht erfolgreich. Ihre Bestellung wurde daher durch uns storniert. Die Rückerstattung erfolgt auf das beim Kauf verwendete Zahlungsmittel.',
											},
										],
										currentLabels?.hintMessages,
									)}
								/>
							)}

							{bazgStatus !== 'confirmed' && bazgStatus !== 'cancelled' && (
								<HintMessageV2
									className="mx-1.5 mb-3.5"
									variant="warn"
									hints={getHintContent(
										'swissProductOrderInProgress',
										[
											{
												message:
													'Bestellung in Bearbeitung. Bitte warten Sie auf die Bestätigung über die erfolgreiche Einbuchung beim Schweizer Mautbetreiber. Aktuell haben Sie keine gültige Schweiz Vignette.',
											},
										],
										currentLabels?.hintMessages,
									)}
								/>
							)}

							{bazgStatus === 'confirmed' && !cancelled && !cancelable && (
								<HintMessageV2
									className="mx-1.5 mb-3.5"
									variant="info"
									hints={getHintContent(
										changelicenseplate || changevalidity ? 'productNotCancelable' : 'productNotAmendable',
										renderHintMessage(changelicenseplate || changevalidity),
										currentLabels?.hintMessages,
									)}
								/>
							)}
						</>
					)}

					{children}
				</div>
			)}
		</div>
	);
};
